// Section 8 and 12: Healer. Treats 3 sick or injured per season (6 with a clinic, via the clinic's
// output modifier); the slots are the healingSlots service, used by the bodies phase's triage.
// Gathers 0.5 medicine (1.5x if literate) only when no one is left to treat this season.
// Treatment halves the death chance (with medicine: config.illness.treatedDeathMul, plague uses
// its own 10%; without medicine: noMedicineDeathMul); a visit to a healthy person with all needs
// met gives +8 health instead of +4. Catching what they treat (3%, plague 10%) is the catchRisk
// target, rolled by the bodies phase, so the job has no production-time risk.
// Constants: config.jobs.healer, config.illness.
import type { Config } from '../../config';
import { alwaysEligible } from '../../core/content-helpers';
import { mod } from '../../core/modifiers';
import { patientCount } from '../../core/queries';
import { defineJob } from '../../core/registry';
import type { Delta } from '../../core/types';

export function healer(config: Config) {
  const c = config.jobs.healer;
  const ill = config.illness;

  return defineJob({
    id: 'healer',
    label: 'Healer',
    section: 'S8',
    heavy: false,
    output: { kind: 'service', service: 'healingSlots' },
    baseYield: c.treats,
    toolFactor: null,
    toolWear: 0,
    risks: {},
    riskIllness: null,
    requiresBuilding: null,
    minStrength: 0,
    modifierTargets: ['illnessDeath', 'healthGain'],
    modifiers(ctx, target) {
      if (target.kind !== 'illnessDeath' && target.kind !== 'healthGain') return [];
      const care = ctx.state.currentReport?.care?.[target.personId];
      if (care === undefined) return [];
      if (target.kind === 'healthGain') {
        // Visits go only to people with all needs met, who already get +4 (needs contributor).
        return care === 'visit'
          ? [
              mod.add(
                'job:healer',
                ill.healerVisitHealth - config.bodies.healthAllNeedsMet,
                'healer visit',
              ),
            ]
          : [];
      }
      if (care === 'medicine') {
        // Illnesses with their own treated chance (plague 10%) set it themselves.
        return 'deathTreated' in config.illnesses[target.illnessId]
          ? []
          : [mod.mul('job:healer', ill.treatedDeathMul, 'treated')];
      }
      if (care === 'no-medicine') {
        return [mod.mul('job:healer', ill.noMedicineDeathMul, 'treated without medicine')];
      }
      return [];
    },
    produce(ctx, worker, output) {
      if (output <= 0) return [];
      const deltas: Delta[] = [{ kind: 'service', service: 'healingSlots', amount: output }];
      // Healers work in id order; this one gathers when the slots before it already cover
      // everyone waiting for treatment. Gathering scales with productivity up to the full 0.5
      // (a clinic doubles treatment, not gathering; C-07).
      const before = ctx.state.currentReport?.services.healingSlots ?? 0;
      const waiting = patientCount(ctx.state, ctx.season.turn);
      if (before + 1e-9 >= waiting) {
        const productive = Math.min(1, output / c.treats);
        const literate = worker.status.literate ? c.literateGatherMul : 1;
        deltas.push({
          kind: 'stock',
          resource: 'medicine',
          amount: c.gatherMedicine * productive * literate,
          flow: 'production',
        });
      }
      return deltas;
    },
    eligible: alwaysEligible,
  });
}
